import { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import { Box, Card, Divider, IconButton, InputAdornment, Switch, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { Editor } from '@tinymce/tinymce-react';
import { Formik, useFormikContext } from 'formik';
import {
  getGetFeatureConfigsPublicQueryKey,
  getGetFeatureConfigsQueryKey,
  useCreateFeatureConfig,
  useCreateTermsOfUseLog,
  useUpdateSiteFeatureConfig,
} from '@juno/client-api';
import { FeatureConfig, FeatureConfigConfig, FeatureConfigTypeEnum } from '@juno/client-api/model';
import { TINY_MCE_DEFAULT_CONFIG } from '@juno/constants';
import { GenericFormikInput, JunoDialog } from '@juno/ui';
import { MutationAction, onMutation, uploadTinyMceImageCloudinary, useSettings } from '@juno/utils';

interface TermsOfUseConfigConfig extends FeatureConfigConfig {
  title: string;
  body: string;
  enable_tou: boolean;
}
export interface TermsOfUseConfig extends FeatureConfig {
  config: TermsOfUseConfigConfig;
}

interface FormValues {
  enable_tou: boolean;
  title: string;
  body: string;
}

const TermsOfUseFormInner = ({
  setUnsavedChanges,
  isSaving,
  last_update,
}: {
  setUnsavedChanges: (value: boolean) => void;
  isSaving: boolean;
  last_update: string | null;
}) => {
  const { values, dirty, setFieldValue, isValid, handleSubmit } = useFormikContext<FormValues>();

  useEffect(() => {
    setUnsavedChanges?.(dirty);
  }, [dirty, setUnsavedChanges]);

  const [openToUDialog, setOpenToUDialog] = useState(false);

  return (
    <>
      <Card sx={{ p: 2, mt: 3, mb: 4, pb: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant='h5' sx={{ mb: 0 }}>
            Terms of Use
          </Typography>
          <Switch
            value={values.enable_tou}
            checked={values.enable_tou}
            onChange={(e) => {
              setFieldValue('enable_tou', e.target.checked);
            }}
          />
        </Box>
        <Divider sx={{ my: 2, mx: -0, mb: 4 }} />
        {values.enable_tou && (
          <>
            <Typography variant='body2' sx={{ mb: 5, mt: 1 }}>
              This prompt will display to users upon first login. Users will not be able to access
              the site until they accept the terms of use. This is a legal requirement for many
              sites.
            </Typography>
            <GenericFormikInput
              label='Dialog Title'
              type='text'
              name='title'
              value={values.title}
              InputProps={{
                endAdornment: values.title ? (
                  <InputAdornment position='end'>
                    <IconButton
                      size='small'
                      onClick={() => {
                        setFieldValue('title', '');
                      }}
                    >
                      <CloseIcon fontSize='small' />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            <Editor
              apiKey={process.env['NX_TINY_MCE_API_KEY']}
              onEditorChange={(value: string) => setFieldValue('body', value)}
              value={values.body || ''}
              init={{
                ...TINY_MCE_DEFAULT_CONFIG,
                images_upload_handler: uploadTinyMceImageCloudinary,
                setup: (editor) => {
                  editor.setProgressState(true);
                },
              }}
            />
          </>
        )}

        <Box
          sx={{
            textAlign: 'right',
            mt: 2,
            display: last_update ? 'flex' : 'block',
            justifyContent: last_update && 'space-between',
          }}
        >
          {last_update && (
            <Typography variant='caption'>
              <i>Last updated: {last_update}</i>
            </Typography>
          )}
          <LoadingButton
            variant='contained'
            color='primary'
            aria-label='Save Terms of Use'
            sx={{ mb: 2 }}
            disabled={!isValid || !dirty || (values.enable_tou && (!values.title || !values.body))}
            onClick={() => (values.enable_tou ? setOpenToUDialog(true) : handleSubmit())}
            loading={isSaving}
          >
            Save
          </LoadingButton>
        </Box>
      </Card>
      {openToUDialog && (
        <JunoDialog
          id='terms-of-use-dialog'
          mode='confirmation'
          title='Terms of Use changing after users accepted'
          subtitle='You have edited the terms of use your users have already accepted.'
          message='By clicking save, all users will be required to reaccept the updated terms of use the next time they log in. Do you wish to continue?'
          open={openToUDialog}
          primaryBtnProps={{
            title: 'Save',
            onClick: () => {
              handleSubmit();
              setOpenToUDialog(false);
            },
          }}
          secondaryBtnProps={{
            title: 'Cancel',
            onClick: () => setOpenToUDialog(false),
          }}
        />
      )}
    </>
  );
};

const TermsOfUseForm = ({ setUnsavedChanges }: { setUnsavedChanges: (value: boolean) => void }) => {
  const { configs, site } = useSettings();
  const touFeatureConfig = configs?.find(
    (config) => config.type === FeatureConfigTypeEnum.terms_of_use,
  ) as TermsOfUseConfig;

  const [isSaving, setIsSaving] = useState(false);

  const queryClient = useQueryClient();
  const refetchConfigs = () => {
    queryClient.invalidateQueries({
      queryKey: getGetFeatureConfigsQueryKey(site?.id || ''),
    });
    queryClient.invalidateQueries({
      queryKey: getGetFeatureConfigsPublicQueryKey(site?.id || ''),
    });
  };

  const updateConfig = useUpdateSiteFeatureConfig(
    onMutation(MutationAction.UPDATE, 'Terms of Use', refetchConfigs),
  );
  const createConfig = useCreateFeatureConfig(
    onMutation(MutationAction.CREATE, 'Terms of Use', refetchConfigs),
  );
  const createTermsOfUseLog = useCreateTermsOfUseLog(
    onMutation(MutationAction.CREATE, 'Terms of Use update Log', refetchConfigs),
  );

  const initialValues = {
    enable_tou: touFeatureConfig?.config?.enable_tou || false,
    title: touFeatureConfig?.config?.title || '',
    body: touFeatureConfig?.config?.body || '',
    force_reacceptance: false,
  };

  let last_update = null;
  if (touFeatureConfig?.config?.enable_tou && touFeatureConfig?.date_updated) {
    last_update = new Date(touFeatureConfig?.date_updated).toLocaleDateString('en-US');
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={async (values) => {
          try {
            setIsSaving(true);
            if (touFeatureConfig) {
              await updateConfig.mutateAsync({
                siteId: site?.id || '',
                configType: FeatureConfigTypeEnum.terms_of_use,
                data: { ...touFeatureConfig, config: values },
              });
            } else {
              await createConfig.mutateAsync({
                siteId: site?.id || '',
                data: { type: FeatureConfigTypeEnum.terms_of_use, config: values },
              });
            }
            await createTermsOfUseLog.mutateAsync({
              siteId: site?.id || '',
              data: {
                title: values.title,
                body: values.body,
              },
            });
          } finally {
            setIsSaving(false);
          }
        }}
        enableReinitialize
      >
        <TermsOfUseFormInner
          setUnsavedChanges={setUnsavedChanges}
          isSaving={isSaving}
          last_update={last_update}
        />
      </Formik>
    </>
  );
};

export default TermsOfUseForm;
